// ────────────────────────────────────────────────
// src/hooks/usePetManager.js   (full file)
// Centralised pet logic + realtime Firestore sync
// ────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import {
  doc, setDoc, updateDoc, deleteDoc,
  collection, addDoc, onSnapshot
} from "firebase/firestore";
import { db } from "../firebase";
import {
  tickPet, feedPet, drinkPet, evolvePet      // ← add evolve helper
} from "../Pet/Pet";

/* ───── helper refs ───── */
const ACTIVE_REF = uid => doc(db, "users", uid, "pets", "current");
const BP_COL     = uid => collection(db, "users", uid, "backpack");

/* =================================================================== */
export const usePetManager = (uid) => {
  /* ---------- state ---------- */
  const [pet,     setPet]  = useState(null);
  const [loading, setLoad] = useState(true);
  const [error,   setErr ] = useState(null);

  /* ---------- 1. live subscription ---------- */
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      ACTIVE_REF(uid),
      snap => { setPet(snap.exists() ? snap.data() : null); setLoad(false); },
      err  => { console.error(err); setErr(err);             setLoad(false); }
    );
  }, [uid]);

  /* ---------- 2. heartbeat (decay + auto-hatch) ---------- */
  useEffect(() => {
    if (!uid || !pet) return;
    const int = setInterval(async () => {
      const next = tickPet(pet);
      if (JSON.stringify(next) !== JSON.stringify(pet)) {
        try { await updateDoc(ACTIVE_REF(uid), next); setPet(next); }
        catch (e) { console.error("tick failed", e); }
      }
    }, 10_000);           // 10 s
    return () => clearInterval(int);
  }, [uid, pet]);

  /* ---------- 3-A  food / water helpers ---------- */
  const hydrate = useCallback(async () => {
    if (!uid || !pet) return;
    const next = drinkPet(pet, { thirst: 25, exp: 1 });
    await updateDoc(ACTIVE_REF(uid), next);
    setPet(next);
  }, [uid, pet]);

  const nourish = useCallback(async () => {
    if (!uid || !pet) return;
    const next = feedPet(pet, { hunger: 25, exp: 3 });
    await updateDoc(ACTIVE_REF(uid), next);
    setPet(next);
  }, [uid, pet]);

  /* ---------- 3-B  original feed / drink ---------- */
  const feed = useCallback(async () => {
    if (!uid || !pet) return;
    const next = feedPet(pet);
    await updateDoc(ACTIVE_REF(uid), next);
    setPet(next);
  }, [uid, pet]);

  const drink = useCallback(async () => {
    if (!uid || !pet) return;
    const next = drinkPet(pet);
    await updateDoc(ACTIVE_REF(uid), next);
    setPet(next);
  }, [uid, pet]);

  /* ---------- 3-C  release / activate ---------- */
  const release = useCallback(async () => {
    if (!uid || !pet) return;
    await addDoc(BP_COL(uid), pet);
    await deleteDoc(ACTIVE_REF(uid));
  }, [uid, pet]);

  const activate = useCallback(
    async (docId, data) => {
      if (!uid || pet) return;              // slot must be empty
      await setDoc(ACTIVE_REF(uid), data);  // overwrite / create
      await deleteDoc(doc(BP_COL(uid), docId));
    },
    [uid, pet]
  );

  /* ---------- 3-D  consume backpack items ---------- */
  const useItem = useCallback(
    async (docId, { sku }) => {
      if (!uid) return;

      if (sku === "spring_water")        await hydrate();
      else if (sku === "berry_snack")    await nourish();
      else if (sku === "evolving_rock") {
        if (!pet || pet.stage !== "baby" || pet.level < 20) return;
        if (!window.confirm("Evolve your pet to Teen form?")) return;
        const next = evolvePet(pet);
        await updateDoc(ACTIVE_REF(uid), next);
        setPet(next);
      }

      await deleteDoc(doc(BP_COL(uid), docId));   // remove item
    },
    [uid, pet, hydrate, nourish]
  );

  /* ---------- export ---------- */
  return {
    pet, loading, error,
    feed, drink, hydrate, nourish,
    release, activate, useItem,
  };
};
